import type { TypedDocumentNode } from '@apollo/client';
import { CombinedGraphQLErrors, gql } from '@apollo/client';

/**
 * Arcade's account surface. A Person is the developer account; the business
 * name is the name of the organization Arcade creates alongside it, which the
 * portal never shows as a thing of its own.
 */

export interface Person {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  businessName: string | null;
}

export interface Profile {
  firstName: string;
  lastName: string;
  businessName: string;
}

const PERSON_FIELDS = gql`
  fragment PersonFields on Person {
    id
    email
    firstName
    lastName
    businessName
  }
`;

export const ME: TypedDocumentNode<{ me: Person | null }> = gql`
  query Me {
    me {
      ...PersonFields
    }
  }
  ${PERSON_FIELDS}
`;

export const PERSON_EXISTS: TypedDocumentNode<{ personExists: boolean }, { email: string }> = gql`
  query PersonExists($email: String!) {
    personExists(email: $email)
  }
`;

export const CREATE_PERSON: TypedDocumentNode<
  { createPerson: Person },
  Profile & { email: string }
> = gql`
  mutation CreatePerson(
    $email: String!
    $firstName: String
    $lastName: String
    $businessName: String
  ) {
    createPerson(
      email: $email
      firstName: $firstName
      lastName: $lastName
      businessName: $businessName
    ) {
      ...PersonFields
    }
  }
  ${PERSON_FIELDS}
`;

export const EDIT_PERSON: TypedDocumentNode<{ editPerson: Person }, Profile> = gql`
  mutation EditPerson($firstName: String, $lastName: String, $businessName: String) {
    editPerson(firstName: $firstName, lastName: $lastName, businessName: $businessName) {
      ...PersonFields
    }
  }
  ${PERSON_FIELDS}
`;

/** The `extensions.code` Arcade attached to a failure, if it was a GraphQL one. */
export function errorCode(error: unknown): string | undefined {
  if (!CombinedGraphQLErrors.is(error)) {
    return undefined;
  }
  const code = error.errors[0]?.extensions?.code;
  return typeof code === 'string' ? code : undefined;
}

export function displayName(person: Pick<Person, 'firstName' | 'lastName' | 'email'>): string {
  const name = [person.firstName, person.lastName].filter(Boolean).join(' ');
  return name || person.email;
}
